import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Button,
  Modal,
  Form,
  Image,
  Badge,
} from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaUpload } from "react-icons/fa";

const AdminTeamManager = () => {
  const [team, setTeam] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    post: "",
    email: "",
    picture: "",
  });
  const [uploading, setUploading] = useState(false);

  const API_URL = "https://radheyelectrical.com/api/about_all.php";
  const blue = "#0062A0";
  const magenta = "#E91E63";

  useEffect(() => {
    fetchTeam();
  }, []);

  const fetchTeam = async () => {
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      setTeam(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Fetch error:", error);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        // Use window.Image to avoid conflict with React-Bootstrap's Image component
        const img = new window.Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const MAX_WIDTH = 400;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
          setFormData({ ...formData, picture: dataUrl });
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOpenModal = (member = null) => {
    if (member) {
      setEditingMember(member);
      setFormData({
        id: member.id,
        name: member.name,
        post: member.post,
        email: member.email,
        picture: member.picture,
      });
    } else {
      setEditingMember(null);
      setFormData({ name: "", post: "", email: "", picture: "" });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    const method = editingMember ? "PUT" : "POST";

    try {
      const res = await fetch(API_URL, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const result = await res.json();
      if (result.message) {
        fetchTeam();
        setShowModal(false);
      }
    } catch (error) {
      alert("Error saving data");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure?")) {
      await fetch(`${API_URL}?id=${id}`, { method: "DELETE" });
      fetchTeam();
    }
  };

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold" style={{ color: blue }}>
          Team Management
        </h2>
        <Button
          onClick={() => handleOpenModal()}
          style={{ backgroundColor: magenta, border: "none" }}
        >
          <FaPlus /> Add New Member
        </Button>
      </div>

      <Table responsive hover className="shadow-sm border">
        <thead style={{ backgroundColor: blue, color: "#fff" }}>
          <tr>
            <th>Member</th>
            <th>Designation</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {team.map((m) => (
            <tr key={m.id} className="align-middle">
              <td>
                <div className="d-flex align-items-center gap-3">
                  <Image
                    src={m.picture || "https://via.placeholder.com/50"}
                    roundedCircle
                    style={{
                      width: "40px",
                      height: "40px",
                      objectFit: "cover",
                    }}
                  />
                  {m.name}
                </div>
              </td>
              <td>
                <Badge bg="light" className="text-dark border">
                  {m.post}
                </Badge>
              </td>
              <td>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="me-2"
                  onClick={() => handleOpenModal(m)}
                >
                  <FaEdit />
                </Button>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => handleDelete(m.id)}
                >
                  <FaTrash />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingMember ? "Update Member" : "Add Member"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {/* Image Preview Area */}
            <div className="text-center mb-3">
              <Image
                src={formData.picture || "https://via.placeholder.com/100"}
                roundedCircle
                style={{
                  width: "100px",
                  height: "100px",
                  objectFit: "cover",
                  border: `2px solid ${blue}`,
                }}
              />
            </div>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Profile Photo</Form.Label>
              <div className="input-group">
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <span className="input-group-text">
                  <FaUpload />
                </span>
              </div>
              <Form.Text className="text-muted">
                Upload a professional square headshot.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Full Name</Form.Label>
              <Form.Control
                required
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Post</Form.Label>
              <Form.Control
                required
                type="text"
                value={formData.post}
                onChange={(e) =>
                  setFormData({ ...formData, post: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-bold small">Email</Form.Label>
              <Form.Control
                required
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading}
              style={{ backgroundColor: blue, border: "none" }}
            >
              {uploading ? "Saving..." : editingMember ? "Update" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AdminTeamManager;
